import { allProducts, productCategories } from '@/data/products';
import { allProductConfigs } from '@/components/products/enhanced-features';

export interface SearchItem {
  id: string;
  title: string;
  description: string;
  category: string;
  type: 'product' | 'page' | 'feature' | 'category';
  url: string;
  keywords: string[];
  icon?: string;
  badges?: string[];
  priority: number;
}

// Website pages to include in search
const websitePages: SearchItem[] = [
  {
    id: 'homepage',
    title: 'Homepage',
    description: 'Dialgia Group - AI-powered business solutions for SMEs',
    category: 'Navigation',
    type: 'page',
    url: '/',
    keywords: ['home', 'main', 'overview', 'dialgia', 'ai', 'solutions'],
    priority: 10
  },
  {
    id: 'products-page',
    title: 'Products',
    description: 'Explore our 353 AI-powered business solutions',
    category: 'Navigation',
    type: 'page',
    url: '/products',
    keywords: ['products', 'solutions', 'browse', 'catalog'],
    priority: 9
  },
  {
    id: 'portfolio',
    title: 'Portfolio',
    description: 'Our success stories and case studies',
    category: 'Navigation',
    type: 'page',
    url: '/portfolio',
    keywords: ['portfolio', 'case studies', 'success', 'examples'],
    priority: 7
  },
  {
    id: 'pricing',
    title: 'Pricing',
    description: 'Transparent pricing for all our solutions',
    category: 'Navigation',
    type: 'page',
    url: '/pricing',
    keywords: ['pricing', 'cost', 'plans', 'subscription'],
    priority: 8
  },
  {
    id: 'contact',
    title: 'Contact Us',
    description: 'Get in touch with our team',
    category: 'Navigation',
    type: 'page',
    url: '/contact',
    keywords: ['contact', 'support', 'help', 'reach'],
    priority: 6
  },
  {
    id: 'website-wizard',
    title: 'Project Wizard',
    description: 'Create your custom business solution',
    category: 'Tools',
    type: 'page',
    url: '/website-wizard',
    keywords: ['wizard', 'create', 'custom', 'builder', 'project'],
    priority: 8
  },
  {
    id: 'admin-portal',
    title: 'Admin Portal',
    description: 'Administrative dashboard and management',
    category: 'Admin',
    type: 'page',
    url: '/admin',
    keywords: ['admin', 'dashboard', 'management', 'portal'],
    priority: 3
  }
];

// Build comprehensive search index
export const buildSearchIndex = (): SearchItem[] => {
  const searchItems: SearchItem[] = [];

  // Add website pages
  searchItems.push(...websitePages);

  // Add product categories
  productCategories.forEach(category => {
    searchItems.push({
      id: `category-${category.id}`,
      title: category.name,
      description: category.description,
      category: 'Product Categories',
      type: 'category',
      url: `/products?category=${category.id}`,
      keywords: [category.name.toLowerCase(), category.description.toLowerCase()],
      icon: category.icon,
      badges: [`${category.productCount} products`],
      priority: 5
    });
  });

  // Add all products with enhanced features
  allProducts.forEach(product => {
    const productConfig = allProductConfigs[product.id];
    const features = productConfig ? [
      ...(productConfig.keyBenefits || []),
      ...(productConfig.advancedCapabilities || []),
      ...(productConfig.securityPerformance || [])
    ] : [];

    // Extract keywords from product
    const keywords = [
      product.title.toLowerCase(),
      product.category.toLowerCase(),
      product.description.toLowerCase(),
      ...(product.businessTypes || []).map(tag => tag.toLowerCase()),
      ...features.map(f => f.title.toLowerCase())
    ];

    // Add main product
    searchItems.push({
      id: product.id,
      title: product.title,
      description: product.description,
      category: product.category,
      type: 'product',
      url: `/products/${product.id}`,
      keywords,
      badges: [product.status, `${features.length} features`],
      priority: product.status === 'Live' ? 10 : product.status === 'Available' ? 8 : 6
    });

    // Add individual features as searchable items
    features.forEach((feature, index) => {
      searchItems.push({
        id: `${product.id}-feature-${index}`,
        title: feature.title,
        description: feature.description,
        category: `${product.title} Features`,
        type: 'feature',
        url: `/products/${product.id}#feature-${index}`,
        keywords: [feature.title.toLowerCase(), feature.description.toLowerCase()],
        priority: 4
      });
    });
  });

  return searchItems;
};

// Fuzzy search implementation
export const fuzzySearch = (query: string, items: SearchItem[], limit = 20): SearchItem[] => {
  const normalizedQuery = query.toLowerCase().trim();
  
  if (!normalizedQuery) return [];

  const results = items.map(item => {
    let score = 0;
    
    // Exact title match gets highest score
    if (item.title.toLowerCase() === normalizedQuery) {
      score += 1000;
    }
    
    // Title starts with query
    else if (item.title.toLowerCase().startsWith(normalizedQuery)) {
      score += 500;
    }
    
    // Title contains query
    else if (item.title.toLowerCase().includes(normalizedQuery)) {
      score += 200;
    }
    
    // Description contains query
    if (item.description.toLowerCase().includes(normalizedQuery)) {
      score += 100;
    }
    
    // Keywords match
    item.keywords.forEach(keyword => {
      if (keyword.includes(normalizedQuery)) {
        score += 50;
      }
    });
    
    // Category match
    if (item.category.toLowerCase().includes(normalizedQuery)) {
      score += 75;
    }
    
    // Priority boost
    score += item.priority * 10;
    
    return { ...item, score };
  })
  .filter(item => item.score > 0)
  .sort((a, b) => b.score - a.score)
  .slice(0, limit);

  return results;
};

// Get autocomplete suggestions
export const getAutocompleteSuggestions = (query: string, items: SearchItem[]): string[] => {
  const normalizedQuery = query.toLowerCase().trim();
  
  if (!normalizedQuery || normalizedQuery.length < 2) return [];

  const suggestions = new Set<string>();
  
  items.forEach(item => {
    // Add matching titles
    if (item.title.toLowerCase().includes(normalizedQuery)) {
      suggestions.add(item.title);
    }
    
    // Add matching keywords
    item.keywords.forEach(keyword => {
      if (keyword.includes(normalizedQuery) && keyword.length > normalizedQuery.length) {
        // Capitalize first letter for better presentation
        const formattedKeyword = keyword.charAt(0).toUpperCase() + keyword.slice(1);
        suggestions.add(formattedKeyword);
      }
    });
  });
  
  return Array.from(suggestions).slice(0, 8);
};

// Category-based filtering
export const filterByCategory = (items: SearchItem[], category: string): SearchItem[] => {
  if (!category || category === 'all') return items;
  return items.filter(item => item.type === category);
};

// Popular searches (static for now, could be dynamic)
export const popularSearches = [
  'CRM solutions',
  'Healthcare management',
  'E-commerce platform',
  'Project management',
  'AI automation',
  'Data analytics',
  'HR management',
  'Financial solutions'
];

// Recent searches storage key
export const RECENT_SEARCHES_KEY = 'dialgia_recent_searches';

// Save recent search
export const saveRecentSearch = (query: string) => {
  if (!query.trim()) return;
  
  try {
    const recent = getRecentSearches();
    const updated = [query, ...recent.filter(s => s !== query)].slice(0, 5);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  } catch (error) {
    console.warn('Failed to save recent search:', error);
  }
};

// Get recent searches
export const getRecentSearches = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to load recent searches:', error);
    return [];
  }
};

// Clear recent searches
export const clearRecentSearches = () => {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch (error) {
    console.warn('Failed to clear recent searches:', error);
  }
};